import { ReactNode, useState } from "react";
import { Col, Flex, Row } from "antd";
import {
  BellTwoTone,
  CalculatorTwoTone,
  ContactsTwoTone,
  CreditCardTwoTone,
  FileTextTwoTone,
  FlagTwoTone,
  GiftTwoTone,
  IdcardTwoTone,
  InteractionTwoTone,
  LockTwoTone,
  MessageTwoTone,
  ProfileTwoTone,
  SafetyCertificateTwoTone,
  TagTwoTone,
} from "@ant-design/icons";
import LangToggle from "components/LangToggle.component";
import { HubGroup, HubRow } from "components/HubGroup.component";
import PageHeading from "components/PageHeading.component";
import DataMigrationModal from "pages/settings/DataMigrationModal.component";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useLoyaltyConfig } from "hooks/queries/loyalty";
import { useReferralConfig } from "hooks/queries/referral";
import { getAllPaymentMethods } from "hooks/queries/payment-methods";
import { getAllDiscountCodes } from "hooks/queries/discount-code";
import { getBusinessById } from "hooks/queries/business";
import { useAuth } from "context/AuthContextProvider";
import { useAdminCapabilities } from "hooks/useAdminCapabilities";
import { isManager } from "shared/utils/role";
import {
  ADMIN_RESTRICTION_FIELD,
  AdminCapability,
  CAPABILITY_ORDER,
} from "shared/utils/capabilities";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { formatPhone } from "shared/utils/phone";
import { LoyaltyProgramStatus, ReferralProgramStatus } from "shared/types/enum";

// Pengaturan is a launcher for everything business-wide: the booking and
// product policies, payment methods, the reward programmes, team settings,
// and the one-time data migration. It used to be a three-column grid of
// loose tiles, which at seventeen entries read as a wall of cards. Now each
// section is one bordered card holding a vertical list of rows, laid out in
// two columns on a wide screen. Every row still shows its current value, so
// the hub stays as informative as the forms behind it.
//
// Data migration belongs here rather than on Kelas or Klien because its output
// feeds BOTH of those importers, so it is nobody's sub-page.

// The two columns are fixed rather than flowed so they end at about the same
// height: Kebijakan, Tim & Akses and Keuangan & Pajak on the left, Reward &
// Promosi and Umum on the right. Below xl they stack in source order.
const Column = ({ children }: { children: ReactNode }) => (
  <Col xs={24} xl={12}>
    <Flex vertical gap={24}>
      {children}
    </Flex>
  </Col>
);

// Metode Pembayaran's meta ("4 Metode Pembayaran") is title case because its
// source string already is; the others compose a lowercase unit word
// ("orang", "hari", "aktif") that's shared with inline sentences elsewhere
// (diff-summary rows, InputNumber addons) where lowercase is correct — so
// rather than recasing those shared keys, capitalize each tile's OWN
// composed meta string right here, matching Metode Pembayaran's look
// without touching how the same words read anywhere else.
const titleCaseMeta = (value: string) =>
  value.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

const SettingsPage = () => {
  const [migrationOpen, setMigrationOpen] = useState(false);
  // Two entries are Manager-only outright: the data migration (one bad
  // import rewrites every client balance) and Pembatasan Admin itself (if an
  // Admin could edit the toggles, every other gate would be decorative).
  const { state } = useAuth();
  const isManagerActor = isManager(state.user?.roles);
  // Laporan Rincian Akun Bulanan exposes real revenue/tax figures, same as
  // every other FinancialAnalytics-gated report — so its tile is hidden for
  // a restricted Admin, not just left to redirect on click.
  const { isRestricted } = useAdminCapabilities();
  const financialRestricted = isRestricted(AdminCapability.FinancialAnalytics);

  // Every tile's current value, so the launcher stays at least as informative
  // as the stacked forms it replaces. useBusinessSettings was already loaded
  // by those forms, so this is not a new request.
  const { data: settings } = useBusinessSettings();
  const { data: loyalty } = useLoyaltyConfig();
  const { data: referral } = useReferralConfig();
  const { data: paymentMethods } = getAllPaymentMethods();
  const { data: discountCodes } = getAllDiscountCodes();
  // Akun Admin's count. Manager-only, so a plain Admin fires no request at all
  // (getBusinessById is enabled on a truthy id). The Manager's own page reads
  // the same cache entry when they open it, so this costs one request, once.
  const { data: business } = getBusinessById(
    isManagerActor ? state.business?.id : undefined,
  );

  // Built at render time, never module scope: translate() is bare i18next.t
  // and resolves before i18n init.
  // The hub's own On/Off words (settings_nav.meta.*), NOT the shared
  // class_settings.on/off that the policy forms and confirm modals use. The
  // Indonesian hub shows the On/Off loanwords for a short, even value column;
  // every other surface keeps Aktif/Nonaktif. Scoped on purpose, so changing
  // one never restyles the other.
  const on = translate("settings_nav.meta.on");
  const off = translate("settings_nav.meta.off");

  // undefined while a query is in flight, which renders no value at all. The
  // meta slot reserves its width either way, so nothing reflows when the value
  // lands. A skeleton would be louder than the thing it stands in for.
  // One thing per value: "Off", or the live value on its own (a value already
  // implies "on"), or "On" when there is nothing more to say. Never "On · 5%".
  // Counts only codes usable RIGHT NOW — a scheduled or expired code is not
  // something the admin can hand out today.
  const discountCodesMeta = discountCodes
    ? titleCaseMeta(
        `${discountCodes.filter((c) => c.effective_status === "active").length} ${translate("discountCodes.meta_active")}`,
      )
    : undefined;
  const paymentMethodsMeta = paymentMethods
    ? `${paymentMethods.length} ${translate("paymentMethods.card_title")}`
    : undefined;
  // The number itself while on: it is the one fact an admin opens the page
  // to check, and a number already implies "on".
  const whatsappMeta = settings
    ? settings.whatsapp_enabled && settings.whatsapp_number
      ? formatPhone(settings.whatsapp_number)
      : off
    : undefined;
  // One phrase, value plus qualifier (the hub's own rule: never "On · 5%").
  // The mode belongs here because it decides what a client is charged, and
  // until now it was invisible until the page was opened.
  const taxMeta = settings
    ? settings.tax_enabled
      ? titleCaseMeta(
          `${settings.tax_rate}% ${translate(`class_settings.tax.mode.${settings.tax_mode}`)}`,
        )
      : off
    : undefined;
  // "N/7 On": how many of the seven notification types are on. Stays
  // informative in every state (7/7, 0/7, anything between). Six while the
  // waitlist is switched off, which hides its notification switch.
  const notificationFlags = settings
    ? [
        settings.notify_clash,
        settings.notify_client_booking,
        ...(settings.waitlist_enabled ? [settings.notify_client_waitlist] : []),
        settings.notify_client_cancel,
        settings.notify_reward_claim,
        settings.notify_package_expiry,
        settings.notify_low_credit,
      ]
    : undefined;
  const notificationsMeta = notificationFlags
    ? titleCaseMeta(
        `${notificationFlags.filter(Boolean).length}/${notificationFlags.length} ${on}`,
      )
    : undefined;
  const loyaltyMeta = loyalty
    ? (loyalty.program?.status ?? LoyaltyProgramStatus.Off) ===
      LoyaltyProgramStatus.Off
      ? off
      : on
    : undefined;
  const referralMeta = referral
    ? (referral.program?.status ?? ReferralProgramStatus.Off) ===
      ReferralProgramStatus.Off
      ? off
      : on
    : undefined;
  const employeeManagementMeta = settings
    ? settings.employee_management_enabled
      ? on
      : off
    : undefined;
  // "Nonaktif" sits in the quieter grey so the column scans as "what's on".
  const mutedIfOff = (meta: string | undefined) => meta === off;

  // "6 Akun": the count-plus-unit shape Metode Pembayaran already uses, with
  // the row's own title supplying the "Admin" half so the value column stays
  // short. title.accounts is the same word the page itself is headed with.
  // ACTIVE accounts only. The page's own table hides deactivated ones behind
  // its Status filter (defaultFilteredValue: ["active"]), so counting them
  // here would promise more rows than the page opens with. Same rule as Kode
  // Diskon's "usable right now" count above.
  const adminAccountsMeta = business?.admins
    ? `${business.admins.filter((a) => a.is_active).length} ${translate("title.accounts")}`
    : undefined;

  // "N/7 dibatasi": how many capabilities plain Admins currently lose.
  const restrictedMeta = settings
    ? titleCaseMeta(
        `${
          CAPABILITY_ORDER.filter((cap) => settings[ADMIN_RESTRICTION_FIELD[cap]])
            .length
        }/${CAPABILITY_ORDER.length} ${translate("admin_restrictions.meta_restricted")}`,
      )
    : undefined;

  return (
    <>
      <PageHeading title={translate("class_settings.tab.settings")} />

      <Row gutter={[24, 24]} style={{ marginTop: 8 }}>
        <Column>
          <HubGroup label={translate("settings_nav.section.policy")}>
            {/* One page for the six booking and product rules (2026-10-07);
                the page itself shows every current value. */}
            <HubRow
              tone="navy"
              icon={<ProfileTwoTone />}
              title={translate("class_settings.booking_policies.title")}
              description={translate("settings_nav.desc.booking_policies")}
              reserveMeta={false}
              to={PATHS.SETTINGS_POLICIES}
            />
          </HubGroup>

          {/* Pembatasan Admin is Manager-only: an Admin who could edit the
              toggles would make every other gate decorative. */}
          <HubGroup label={translate("settings_nav.section.team")}>
            <HubRow
              tone="purple"
              icon={<IdcardTwoTone />}
              title={translate("class_settings.employee_management.title")}
              description={translate("settings_nav.desc.employee_management")}
              meta={employeeManagementMeta}
              muted={mutedIfOff(employeeManagementMeta)}
              to={PATHS.SETTINGS_EMPLOYEE_MANAGEMENT}
            />
            {isManagerActor && (
              <HubRow
                tone="purple"
                icon={<LockTwoTone />}
                title={translate("admin_restrictions.title")}
                description={translate("settings_nav.desc.admin_restrictions")}
                meta={restrictedMeta}
                to={PATHS.SETTINGS_ADMIN_RESTRICTIONS}
              />
            )}
            {/* Who the admins are, directly under what they may do. Same
                Manager gate the old top-level sidebar item carried; the page
                keeps its own /accounts route. */}
            {isManagerActor && (
              <HubRow
                tone="purple"
                icon={<SafetyCertificateTwoTone />}
                title={translate("admin_accounts")}
                description={translate("settings_nav.desc.admin_accounts")}
                meta={adminAccountsMeta}
                to={PATHS.ACCOUNTS}
              />
            )}
          </HubGroup>

          <HubGroup label={translate("settings_nav.section.finance")}>
            <HubRow
              tone="green"
              icon={<CreditCardTwoTone />}
              title={translate("paymentMethods.card_title")}
              description={translate("settings_nav.desc.payment_methods")}
              meta={paymentMethodsMeta}
              to={PATHS.SETTINGS_PAYMENT_METHODS}
            />
            <HubRow
              tone="green"
              icon={<CalculatorTwoTone />}
              title={translate("class_settings.tax.title")}
              description={translate("settings_nav.desc.tax")}
              meta={taxMeta}
              muted={mutedIfOff(taxMeta)}
              to={PATHS.SETTINGS_TAX}
            />
            {/* Exposes real revenue/tax figures, same as every other
                FinancialAnalytics-gated report, so a restricted Admin doesn't
                see the row at all rather than being redirected on click. */}
            {!financialRestricted && (
              <HubRow
                tone="green"
                icon={<FileTextTwoTone />}
                title={translate("pdf.monthly_statement.title")}
                description={translate("settings_nav.desc.monthly_statement")}
                to={PATHS.SETTINGS_MONTHLY_STATEMENT}
              />
            )}
          </HubGroup>
        </Column>

        <Column>
          <HubGroup label={translate("settings_nav.section.promo_reward")}>
            <HubRow
              tone="amber"
              icon={<GiftTwoTone />}
              title={translate("loyalty.card_title")}
              description={translate("settings_nav.desc.loyalty")}
              meta={loyaltyMeta}
              muted={mutedIfOff(loyaltyMeta)}
              to={PATHS.SETTINGS_LOYALTY}
            />
            <HubRow
              tone="amber"
              icon={<ContactsTwoTone />}
              title={translate("referral.card_title")}
              description={translate("settings_nav.desc.referral")}
              meta={referralMeta}
              muted={mutedIfOff(referralMeta)}
              to={PATHS.SETTINGS_REFERRAL}
            />
            <HubRow
              tone="amber"
              icon={<TagTwoTone />}
              title={translate("discountCodes.card_title")}
              description={translate("settings_nav.desc.discount_codes")}
              meta={discountCodesMeta}
              to={PATHS.SETTINGS_DISCOUNT_CODES}
            />
          </HubGroup>
          <HubGroup label={translate("settings_nav.section.general")}>
            <HubRow
              tone="neutral"
              icon={<BellTwoTone />}
              title={translate("notification_settings.title")}
              description={translate("settings_nav.desc.notifications")}
              meta={notificationsMeta}
              to={PATHS.SETTINGS_NOTIFICATIONS}
            />
            <HubRow
              tone="neutral"
              icon={<MessageTwoTone />}
              title={translate("class_settings.whatsapp.title")}
              description={translate("settings_nav.desc.whatsapp")}
              meta={whatsappMeta}
              muted={mutedIfOff(whatsappMeta)}
              to={PATHS.SETTINGS_WHATSAPP}
            />
            {/* The one row that opens a flow in place rather than navigating.
                Manager-only (the CSV routes behind its output are too). */}
            {isManagerActor && (
              <HubRow
                tone="neutral"
                icon={<InteractionTwoTone />}
                title={translate("migration.modal_title")}
                description={translate("settings_nav.desc.migration")}
                onClick={() => setMigrationOpen(true)}
              />
            )}
            {/* Nothing to navigate to: the toggle switches the UI language in
                place (LanguageRoot remounts the tree). */}
            <HubRow
              tone="neutral"
              icon={<FlagTwoTone />}
              title={translate("preferences.language")}
              description={translate("preferences.language_sub")}
              control={<LangToggle />}
            />
          </HubGroup>
        </Column>
      </Row>

      <DataMigrationModal
        open={migrationOpen}
        onClose={() => setMigrationOpen(false)}
      />
    </>
  );
};

export default SettingsPage;
